import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DataTable, Drawer, EmptyState, ErrorNotice, LoadingState, SearchInput, TrendChart } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { readPageQueryDetail, type QueryDayRow, type QueryPageRow } from '@/lib/pages';
import { periodLabel } from '@/lib/report-window';
import { STATS_INTERVAL } from '@/lib/leads';

/** Where a page's own analytics live. */
function pageDetailHref(path: string): string {
  return `/analytics/pages/detail?path=${encodeURIComponent(path)}`;
}

function ctrLabel(ctr: number): string {
  return `${(ctr * 100).toFixed(1)}%`;
}

/** The path part of a full URL Search Console reports, for the ranking table. */
export function pathOfUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname === '' ? '/' : parsed.pathname;
  } catch {
    return url;
  }
}

/** A ranking page with its path, for the search. */
interface RankingPageRow extends QueryPageRow {
  readonly path: string;
}

/** The ranking-page fields the search box matches. */
const RANKING_PAGE_SEARCH_FIELDS: ReadonlyArray<keyof RankingPageRow> = ['path', 'page'];

function dayDetails(day: QueryDayRow): string[] {
  return [`${day.impressions.toLocaleString()} impressions`, `${ctrLabel(day.ctr)} CTR`, `position ${day.position.toFixed(1)}`];
}

/**
 * One search query drilled into: its clicks and Google position day by day
 * over the page's selected range, and every page of ours that ranks for it.
 * Opens from a row of the Top search queries table.
 */
export function QueryTrendDrawer({
  path,
  query,
  window,
  rangeLabel,
  onClose,
  onSelectPage,
  onBack,
}: {
  /** One page, or null for the whole site. */
  readonly path: string | null;
  readonly query: string | null;
  readonly window: { readonly from: string; readonly to: string };
  readonly rangeLabel: string;
  readonly onClose: () => void;
  /** When given, a ranking page is a button that narrows the drawer to that page. */
  readonly onSelectPage?: ((path: string) => void) | undefined;
  /** When given, a link back to the site-wide view is shown above the charts. */
  readonly onBack?: (() => void) | undefined;
}): React.ReactElement {
  const detail = useQuery({
    queryKey: ['page-query-detail', path, query, window.from, window.to],
    queryFn: ({ signal }) => readPageQueryDetail({ from: window.from, to: window.to, ...(path === null ? {} : { path }), query: query ?? '' }, signal),
    enabled: query !== null,
  });
  const scope = path === null ? 'across the site' : `for ${path}`;
  // The search belongs to one query: opening another starts it empty, derived
  // here rather than reset in an effect.
  const [pageSearch, setPageSearch] = useState<{ readonly query: string | null; readonly text: string }>({ query: null, text: '' });
  const search = pageSearch.query === query ? pageSearch.text : '';
  const rankingPages = filterRows<RankingPageRow>(
    (detail.data?.pages ?? []).map((row) => ({ ...row, path: pathOfUrl(row.page) })),
    search,
    RANKING_PAGE_SEARCH_FIELDS,
  );

  return (
    <Drawer open={query !== null} onClose={onClose} title={query ?? ''} subtitle={`How this search performed ${scope} over the last ${rangeLabel}`}>
      {onBack && path !== null ? (
        <button type="button" onClick={onBack} className="mb-4 cursor-pointer text-portal-micro font-bold text-brand hover:underline">
          ← All pages for this search
        </button>
      ) : null}
      {detail.isPending ? (
        <LoadingState label="Asking Search Console…" />
      ) : detail.isError ? (
        <ErrorNotice title="Query trend unavailable" message={errorMessage(detail.error)} />
      ) : detail.data.series.length === 0 ? (
        <EmptyState title="No days with impressions" message="Google did not show this page for the query in this range." />
      ) : (
        <div className="space-y-6">
          <section>
            <h3 className="text-portal-card-title font-bold text-portal-ink">Clicks per day</h3>
            <p className="mb-2 text-portal-micro text-portal-muted">Days Google showed the page for this search.</p>
            <TrendChart
              points={detail.data.series.map((day) => ({ key: day.date, label: periodLabel(day.date, STATS_INTERVAL.day), value: day.clicks, details: dayDetails(day) }))}
              title="Clicks"
              unit="clicks"
              height={200}
            />
          </section>
          <section>
            <h3 className="text-portal-card-title font-bold text-portal-ink">Position per day</h3>
            <p className="mb-2 text-portal-micro text-portal-muted">Average Google position for this search · lower is better.</p>
            <TrendChart
              points={detail.data.series.map((day) => ({ key: day.date, label: periodLabel(day.date, STATS_INTERVAL.day), value: Math.round(day.position * 10) / 10, details: dayDetails(day) }))}
              variant="line"
              title="Position"
              unit="avg position"
              height={200}
              formatValue={(value) => value.toFixed(1)}
            />
          </section>
          <section>
            <h3 className="text-portal-card-title font-bold text-portal-ink">Our pages for this search</h3>
            <p className="mb-2 text-portal-micro text-portal-muted">Every page of ours Google showed for it in this range, most shown first.{onSelectPage ? ' Click one for this search on that page.' : ''}</p>
            {detail.data.pages.length > 0 ? (
              <SearchInput
                label="Search our pages"
                placeholder="Page path"
                value={search}
                onChange={(event) => setPageSearch({ query, text: event.target.value })}
                className="mb-3 w-full max-w-sm"
              />
            ) : null}
            {detail.data.pages.length === 0 ? (
              <EmptyState title="No pages" message="Google showed none of our pages for this search in the range." />
            ) : rankingPages.length === 0 ? (
              <EmptyState title="No pages match" message="Try a different search." />
            ) : (
              <DataTable headers={['Page', 'Clicks', 'Impressions', 'Position']} rightAlignedHeaders={[1, 2, 3]} minWidth={340} flush>
                {rankingPages.map((row) => {
                  const rowPath = row.path;
                  return (
                    <tr key={row.page} className="border-t border-portal-line align-middle">
                      <td className="p-3 text-portal-small font-bold break-words text-portal-ink">
                        {rowPath === path ? (
                          <span>{rowPath} <span className="text-portal-micro font-normal text-portal-muted">(this page)</span></span>
                        ) : onSelectPage ? (
                          <button type="button" onClick={() => onSelectPage(rowPath)} className="cursor-pointer text-left text-brand hover:underline" title="This search on this page">
                            {rowPath}
                          </button>
                        ) : (
                          <Link to={pageDetailHref(rowPath)} className="text-brand hover:underline">{rowPath}</Link>
                        )}
                      </td>
                      <td className="p-3 text-right text-portal-small tabular-nums">{row.clicks.toLocaleString()}</td>
                      <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{row.impressions.toLocaleString()}</td>
                      <td className="p-3 text-right text-portal-small tabular-nums">{row.position.toFixed(1)}</td>
                    </tr>
                  );
                })}
              </DataTable>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}
